import { Rectangle } from 'recharts';
import { ChartDataPoint, ChartSeries, TimePeriod } from '../../../types/insights';

export const isAdoptionsMonthView = (
  type: string | undefined,
  selectedPeriod: string | undefined
): boolean => type === 'adoptions' && selectedPeriod === TimePeriod.MONTH;

export type MonthlyAdoptionPoint = {
  name: string;
  value: number;
  color: string;
  year: number;
  totalAdoptions: number;
  reportMissing?: boolean;
  month: string;
  isSingleMonthView: true;
};

export const buildMonthlyAdoptionChartData = (
  data: ChartDataPoint[],
  series: ChartSeries[]
): MonthlyAdoptionPoint[] => {
  const monthData = data[0] || ({} as ChartDataPoint);

  return series.map((s) => ({
    name: s.label,
    value: Number(monthData[s.key]) || 0,
    color: s.color,
    year: monthData.year,
    totalAdoptions: Number(monthData.totalAdoptions ?? 0),
    reportMissing:
      typeof monthData.reportMissing === 'boolean' ? monthData.reportMissing : undefined,
    month: monthData.month || '',
    isSingleMonthView: true as const,
  }));
};

type YAxisConfig =
  | {
      domain: [number, number];
      ticks: number[];
    }
  | {
      domain: [number, 'auto'];
      ticks: undefined;
    };

export const getYAxisConfig = (
  isChartEmpty: boolean,
  isMonthlyAdoptions: boolean,
  maxValue: number
): YAxisConfig => {
  if (isChartEmpty) {
    return {
      domain: [0, 4],
      ticks: [0, 1, 2, 3, 4],
    };
  }

  if (isMonthlyAdoptions && maxValue > 0) {
    // For small numbers, show every integer up to max + 1
    if (maxValue <= 10) {
      const max = maxValue + 1;
      return {
        domain: [0, max],
        ticks: Array.from({ length: max + 1 }, (_, i) => i),
      };
    }

    // For larger numbers keep 5 intervals, steps divisible by 5
    const intervals = 5;
    const minStep = Math.ceil((maxValue + 1) / intervals);
    const step = Math.ceil(minStep / 5) * 5;
    const max = step * intervals;
    const ticks = Array.from({ length: intervals + 1 }, (_, i) => i * step);

    return { domain: [0, max], ticks };
  }

  return {
    domain: [0, 'auto'],
    ticks: undefined,
  };
};

export const getAdjustedBarPosition = (value: number, height: number, y: number) => {
  const MIN_BAR_HEIGHT = 24;
  if (value >= 1 && height < MIN_BAR_HEIGHT) {
    return {
      adjustedY: y - (MIN_BAR_HEIGHT - height),
      adjustedHeight: MIN_BAR_HEIGHT,
    };
  }
  return { adjustedY: y, adjustedHeight: height };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderMinHeightBar = (props: any) => {
  const { value, height, y, x, width, fill, radius } = props;
  const { adjustedY, adjustedHeight } = getAdjustedBarPosition(value, height, y);

  return (
    <Rectangle
      x={x}
      y={adjustedY}
      width={width}
      height={adjustedHeight}
      fill={fill}
      radius={radius}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderCustomBarLabel = (dy: number) => (props: any) => {
  const { x, y, value, height } = props;
  if (!value) return <g />;

  const { adjustedY } = getAdjustedBarPosition(value, height, y);

  return (
    <text
      x={x + 5}
      y={adjustedY + 5}
      dy={dy}
      fill="#ffffff"
      fontSize={20}
      fontWeight={700}
      textAnchor="start"
    >
      {value}
    </text>
  );
};
